import React, { useState } from 'react';
import { User } from '@/types';
import styles from './W_WalletTab.module.css';
import W_PaymentActions from '@/components/W_Wrappers/W_PaymentActions/W_PaymentActions';
import C_PaySystems from '@/components/C_Collections/C_PaySystems/C_PaySystems';
import A_Input from '@/components/A_Atoms/A_Input/A_Input';
import A_Notification from '@/components/A_Atoms/A_Notification/A_Notification';
import cn from 'classnames';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import { walletApi } from '@/services/walletApi';
import { useAuth } from '@/contexts/AuthContext';

const isAmountValid = (amount: string) => {
  const numAmount = Number(amount.replace(/\s/g, ''));
  return numAmount >= 1 && numAmount <= 1000000 && amount.trim() !== '';
};

interface W_WalletTabProps {
  user: User;
}

export const W_WalletTab: React.FC<W_WalletTabProps> = ({ user }) => {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);
  const [amount, setAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const { getCurrentUser } = useAuth();

  const handleAmountChange = (value: string) => {
    setAmount(value);
    setError(null);
    setShowErrorNotification(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isAmountValid(amount)) {
      const errorMsg = t('payment.wallet.invalidAmount') || 'Неверная сумма';
      setError(errorMsg);
      setShowErrorNotification(true);
      return;
    }

    const numAmount = Number(amount.replace(/\s/g, ''));
    setIsLoading(true);
    setError(null);

    try {
      const response = await walletApi.createTopup(numAmount);

      if (response.success && response.data) {
        const { redirectUrl, paymentId, status } = response.data;

        if (redirectUrl) {
          window.location.assign(redirectUrl);
          return;
        }

        if (status === 'succeeded') {
          await getCurrentUser();
        }

        setError(null);
        const successMsg =
          t('payment.wallet.created') ||
          `Пополнение создано (ID: ${paymentId}). Статус: ${status}.`;
        setSuccessMessage(successMsg);
        setShowSuccessNotification(true);
        setAmount('');
      } else {
        const errorMsg =
          response.message ||
          t('payment.wallet.error') ||
          'Ошибка при создании пополнения';
        setError(errorMsg);
        setShowErrorNotification(true);
      }
    } catch (err: any) {
      const errorMsg =
        err.message ||
        t('payment.wallet.error') ||
        'Ошибка при создании пополнения';
      setError(errorMsg);
      setShowErrorNotification(true);
    } finally {
      setIsLoading(false);
    }
  };

  const numAmount = amount ? Number(amount.replace(/\s/g, '')) : 0;

  return (
    <div className={styles.wrapper}>
      <form className={styles.content} onSubmit={handleSubmit}>
        <h4 className={styles.title}>{t('payment.wallet.enterAmountTitle')}</h4>
        <div className={styles.input_wrapper}>
          <span className={cn(styles.input_label, 'text-p-2')}>
            {t('payment.wallet.rangeLabel')}
          </span>
          <A_Input
            label={t('payment.wallet.amountLabel')}
            value={amount}
            onChange={handleAmountChange}
            background='gray'
            disabled={isLoading}
          />
        </div>
        <W_PaymentActions
          price={numAmount}
          isAmountValid={isAmountValid(amount)}
          isLoading={isLoading}
        />
        <C_PaySystems />
      </form>
      <A_Notification
        type='error'
        message={error || ''}
        isVisible={showErrorNotification}
        onClose={() => {
          setShowErrorNotification(false);
          setError(null);
        }}
        duration={5000}
      />
      <A_Notification
        type='success'
        message={successMessage || ''}
        isVisible={showSuccessNotification}
        onClose={() => {
          setShowSuccessNotification(false);
          setSuccessMessage(null);
        }}
        duration={5000}
      />
    </div>
  );
};
